import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { SectionHeader, CTABanner } from "@/components/Shared";
import { Compass, Eye, PenLine, Hammer, KeyRound } from "lucide-react";
import { IMAGES } from "@/data/content";

const STEPS = [
  {
    num: "01",
    icon: Compass,
    title: "Identify",
    summary: "Finding locations and development opportunities with potential.",
    detail: "We study markets, neighbourhoods and land parcels to find opportunities where thoughtful development can create genuine value — from ageing urban buildings suited to redevelopment, to well-connected land suited to plotted layouts.",
  },
  {
    num: "02",
    icon: Eye,
    title: "Evaluate",
    summary: "Understanding feasibility, location and development potential.",
    detail: "Every opportunity is examined carefully: location fundamentals, connectivity, surroundings, regulatory context and commercial feasibility. If an opportunity does not hold up to honest scrutiny, we do not pursue it.",
  },
  {
    num: "03",
    icon: PenLine,
    title: "Plan",
    summary: "Creating thoughtful development strategies.",
    detail: "We develop clear strategies for each opportunity — layout thinking, design direction, stakeholder alignment and a practical execution roadmap — before any development begins.",
  },
  {
    num: "04",
    icon: Hammer,
    title: "Develop",
    summary: "Working towards quality execution and responsible development.",
    detail: "Execution is where intent becomes reality. We pursue quality at every stage, work with capable partners and hold the development process to the standard our long-term vision demands.",
  },
  {
    num: "05",
    icon: KeyRound,
    title: "Create Value",
    summary: "Building opportunities with a long-term perspective.",
    detail: "Our measure of success is lasting value — for the people who live in or use our developments, for the landowners and partners who trusted us, and for the company we are building.",
  },
];

const Approach = () => (
  <>
    <SEO
      title="Our Approach | Evaya Ventures — Real Estate Development Process"
      description="Evaya Ventures follows a considered five-step development process: Identify, Evaluate, Plan, Develop and Deliver Value."
      path="/approach"
      breadcrumbs={[{ name: "Home", path: "/" }, { name: "Our Approach", path: "/approach" }]}
    />

    <PageHero
      overline="From Earth to Extraordinary"
      title="From Potential to Possibility"
      description="Every opportunity we pursue moves through the same five-step journey. It keeps us honest, keeps our stakeholders informed and keeps quality at the centre of what we do."
      image={IMAGES.blueprints}
      imageAlt={IMAGES.blueprintsAlt}
      testId="approach-hero"
    />

    <section className="bg-white py-24 lg:py-32" data-testid="approach-steps">
      <div className="mx-auto max-w-5xl px-6 lg:px-12">
        <ol className="relative space-y-0 border-l border-ink/15">
          {STEPS.map((step, i) => (
            <li key={step.title} className="relative pb-16 pl-10 last:pb-0 md:pl-14">
              <span className="absolute -left-[9px] top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-brand bg-white" aria-hidden="true" />
              <Reveal delay={Math.min(i * 0.05, 0.15)}>
                <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
                  <span className="font-display text-5xl font-light text-brand">{step.num}</span>
                  <h2 className="font-display text-3xl text-ink md:text-4xl" data-testid={`approach-step-${step.num}-title`}>{step.title}</h2>
                </div>
                <p className="mt-4 max-w-2xl text-base font-medium leading-relaxed text-ink/80 md:text-lg">{step.summary}</p>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/60">{step.detail}</p>
              </Reveal>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="border-t border-ink/10 bg-sand py-24 lg:py-32" data-testid="approach-principles">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
        <Reveal className="lg:col-span-5">
          <img src={IMAGES.planningHands} alt={IMAGES.planningHandsAlt} loading="lazy" className="h-[420px] w-full object-cover" />
        </Reveal>
        <div className="lg:col-span-7 lg:pl-8">
          <SectionHeader
            overline="Why It Matters"
            title="Process is how a young company earns trust"
            description="Evaya Ventures is building its reputation from the ground up. A clear, repeatable development process is how we make sure every opportunity receives the rigour it deserves — and how our stakeholders always know where things stand."
            testId="approach-principles"
          />
        </div>
      </div>
    </section>

    <CTABanner title="See how our approach could work for you" copy="Whether you have land, a redevelopment idea or an investment interest, we would be glad to walk you through how we work." />
  </>
);

export default Approach;
